import type { GraphemeLessonPart } from "@-/dev.improve.haniverse.lib.level";
import { watchAsyncStep } from "@-/dev.improve.haniverse.lib.async-watchdog";
import type {
  NextDueUserCardData,
  NextDueUserCardDataOverride,
} from "@-/dev.improve.haniverse.lib.cards.study-card-data";
import type { PronouncedGraphemes } from "@-/dev.improve.haniverse.lib.cards.user-card.grapheme.pronunciation.read";
import {
  getMorphemeLessonGroupIndex,
  getNextSlideSlugInOrder,
  getQuizUnit,
  getSlideUnit,
  isLessonGroupBoundary,
  type Unit,
} from "@-/dev.improve.haniverse.lib.lesson.get";
import type { Level } from "@-/dev.improve.haniverse.lib.level";
import {
  getQuizManifestEntryByKey,
  getQuizManifestEntryBySlug,
  getSlideKeyBySlug,
} from "@-/dev.improve.haniverse.assets.slides";

import type { LearnCardDestination, LearnCardDestinationOrigin } from "#learn-card-destination";
import { readThroughCancellation } from "#read-through-cancellation";

/**
 * The reads the resolver needs, handed in by the caller so the decision is pure over them. In
 * the app they are `fetchQuery`s over the registered query options (freshness-guarded, so a read
 * that runs moments after a rating commit cannot observe a pre-commit snapshot); in a test they
 * are whatever the scenario needs.
 */
export type LearnCardDestinationReads = {
  fetchNextDueUserCard: (
    override: NextDueUserCardDataOverride,
  ) => Promise<NextDueUserCardData | null>;
  fetchNextLesson: (override?: {
    afterGrapheme?: { grapheme: string; levelIndex: number; part?: GraphemeLessonPart };
    /**
     * Slides and quizzes (catalogue UUID keys) to count as done whether or not that is written yet.
     */
    assumedCompletedUnitKeys?: readonly string[];
    /** Characters whose meaning lesson counts as studied whether or not that is written yet. */
    assumedStudiedGraphemes?: readonly string[];
  }) => Promise<Unit | null>;
  /** Graphemes / readings with a pronunciation card in study (pronunciation-level lessons behind the learner). */
  fetchPronouncedGraphemes: () => Promise<PronouncedGraphemes>;
  /** Review recharge, 0–100 (see `@-/dev.improve.haniverse.lib.cards.review`). */
  fetchReviewRecharge: () => Promise<number>;
  fetchStudiedGraphemes: () => Promise<ReadonlySet<string>>;
  levels: Level[];
};

export type LearnCardDestinationSettings = {
  /**
   * Not consulted here: an exhausted review queue opens the next lesson group whether or not a character was
   * learned this session ("continues directly to the next lesson even before learning a new
   * character" in the tests).
   */
  hasLearnedNewCharacterThisSession: boolean;
  /** The "Introduce New Cards" option: off means reviews never open a lesson. */
  introduceNewCardsEnabled: boolean;
  /**
   * A review focus (anything but "All" in the Reviews menu) serves only that kind's due reviews —
   * the due reads are already narrowed to it — never a lesson, and ends at the summary when they
   * run out.
   */
  reviewFocusActive: boolean;
  /**
   * The review-recharge "New Cards" toggle. ON spends review recharge on new curriculum cards: a
   * review leads into the next new card while review recharge remains. Revealing a new character
   * drains it to 0, so the unit that character opens is finished and the next boundary returns to
   * reviews.
   */
  reviewRechargeNewCardsEnabled: boolean;
  /** Voice study serves only voice-eligible reviews and ends at the summary when they run out. */
  voiceActive: boolean;
};

export type LearnCardDestinationResolution = {
  destination: LearnCardDestination;
  /** The current due-review queue ran dry during this resolution. */
  dueQueueExhausted: boolean;
};

/**
 * THE "what comes next" decision for the Learn flow — one function for every card kind. The deck's
 * flashcard queue is planned with it: each flashcard is resolved after the queue's tail, counting
 * every card ahead of it (`predictionOrigins`, then `origin`) as already done, and is then the card
 * that follows, whatever the writes ahead of it turn out to change. Without `predictionOrigins` the
 * decision reads committed state alone (the summary's head of the queue).
 *
 * Every read goes through {@link readThroughCancellation}: a cancelled cache read is a superseded
 * read, and the decision must be re-read rather than abandoned.
 */
export async function resolveLearnCardDestination({
  origin,
  predictionOrigins,
  reads,
  settings,
}: {
  origin: LearnCardDestinationOrigin;
  /** Uncommitted preceding cards, used only for speculative lookahead. */
  predictionOrigins?: readonly LearnCardDestinationOrigin[];
  reads: LearnCardDestinationReads;
  settings: LearnCardDestinationSettings;
}): Promise<LearnCardDestinationResolution> {
  // The slides the learner is continuing past count as completed before their completion is written
  // (it is written behind): a prediction made while the learner is still on the last slide of a run
  // would otherwise resolve to that same slide, and the committed decision would have to replace
  // the card prepared underneath. The committed decision names the slide too, which keeps its read
  // identical to the prediction's. A quiz being answered counts as answered the same way.
  const assumedCompletedUnitKeys = [...(predictionOrigins ?? []), origin].flatMap((candidate) => {
    const unitKey =
      candidate.kind === "slide"
        ? getSlideKeyBySlug(candidate.slideSlug)
        : candidate.kind === "quiz"
          ? getQuizManifestEntryBySlug(candidate.quizSlug)?.key
          : undefined;
    return unitKey === undefined ? [] : [unitKey];
  });
  if (assumedCompletedUnitKeys.length > 0) {
    const originalReads = reads;
    reads = {
      ...originalReads,
      fetchNextLesson: (override) =>
        originalReads.fetchNextLesson({ ...override, assumedCompletedUnitKeys }),
    };
  }
  // Likewise the meaning lessons a lookahead is predicting past: a prediction made on a lesson for
  // the card after the slide that follows it would otherwise resolve back to that lesson, and the
  // card shown when the slide is swiped away would be replaced by the right one moments later.
  const assumedStudiedGraphemes = (predictionOrigins ?? []).flatMap((candidate) =>
    candidate.kind === "lesson" && candidate.part !== "pronunciation" ? [candidate.grapheme] : [],
  );
  if (assumedStudiedGraphemes.length > 0) {
    const originalReads = reads;
    reads = {
      ...originalReads,
      fetchNextLesson: (override) =>
        originalReads.fetchNextLesson({ ...override, assumedStudiedGraphemes }),
      fetchStudiedGraphemes: async () =>
        new Set([...(await originalReads.fetchStudiedGraphemes()), ...assumedStudiedGraphemes]),
    };
  }
  // Accepting a new character's lesson while reviews are due drains review recharge to 0. A lesson
  // ahead in the queue (the one being left included) has not been accepted yet, so its drain is
  // counted here: otherwise a lesson-group boundary decided early would still see the charge, and
  // open another unit that the charge no longer pays for.
  if (
    predictionOrigins !== undefined &&
    [...predictionOrigins, origin].some(
      (candidate) => candidate.kind === "lesson" && candidate.part !== "pronunciation",
    )
  ) {
    reads = { ...reads, fetchReviewRecharge: async () => 0 };
  }
  if (predictionOrigins !== undefined) {
    const precedingReviews = [...predictionOrigins, origin].filter(
      (candidate) => candidate.kind === "review",
    );
    const predictionCards = precedingReviews.flatMap((candidate) =>
      candidate.drillType === undefined
        ? []
        : [{ drillType: candidate.drillType, grapheme: candidate.grapheme }],
    );
    // A quiz being answered may still be due until its answer lands; it is already in the deck.
    const predictionQuizKeys = [...predictionOrigins, origin].flatMap((candidate) => {
      const quizKey =
        candidate.kind === "quiz" ? getQuizManifestEntryBySlug(candidate.quizSlug)?.key : undefined;
      return quizKey === undefined ? [] : [quizKey];
    });
    // New characters ahead (the one being left included) whose enrollment has not landed: if they
    // spend today's allowance, what was just learned is revived as reviews, these included, rather
    // than the flow ending at the limit.
    const introducingGraphemes = [...predictionOrigins, origin].flatMap((candidate) =>
      candidate.kind === "lesson" && candidate.part !== "pronunciation" ? [candidate.grapheme] : [],
    );
    const originalReads = reads;
    reads = {
      ...originalReads,
      fetchNextDueUserCard: (override) =>
        originalReads.fetchNextDueUserCard({
          ...override,
          predictionCards,
          predictionExposures: precedingReviews.map((candidate) => candidate.grapheme),
          ...(predictionQuizKeys.length === 0 ? {} : { predictionQuizKeys }),
          ...(introducingGraphemes.length === 0 ? {} : { introducingGraphemes }),
        }),
    };
  }
  // Voice study and a review focus both serve only their narrowed due queue after a review. A focus
  // does so whatever card the learner is leaving: a lesson left open when it was picked hands off
  // to the focused reviews too.
  if (settings.reviewFocusActive || (settings.voiceActive && origin.kind === "review")) {
    return resolveNarrowedReviews({
      grapheme: origin.kind === "review" && origin.grapheme !== "" ? origin.grapheme : undefined,
      reads,
      settings,
    });
  }
  switch (origin.kind) {
    case "review": {
      // An empty grapheme means "nothing to deprioritise" — a voice skip issued before any card
      // was on screen. Mapped here so the read's cache key stays the plain undefined form.
      return resolveAfterReview({
        grapheme: origin.grapheme === "" ? undefined : origin.grapheme,
        reads,
        settings,
      });
    }
    case "lesson": {
      return resolveAfterLesson({
        grapheme: origin.grapheme,
        level: origin.level,
        part: origin.part,
        reads,
        settings,
      });
    }
    case "slide": {
      return resolveAfterSlide({ reads, settings, slideSlug: origin.slideSlug });
    }
    case "quiz": {
      // A due quiz is one more review; a lesson quiz is one more unit of its lesson.
      return origin.source === "review"
        ? resolveAfterReview({ grapheme: undefined, reads, settings })
        : resolveAfterLessonQuiz({ quizSlug: origin.quizSlug, reads, settings });
    }
  }
}

/** The next due card as a destination: a drill reviews it, a quiz is answered. */
function reviewDestination(card: NextDueUserCardData): LearnCardDestination {
  if (card.kind === "drill") {
    return { card, kind: "review" };
  }
  const quiz = getQuizManifestEntryByKey(card.quizKey);
  if (quiz === undefined) {
    // The due reads keep only quizzes this build has (`isCardContentAvailable`).
    throw new Error(`Due quiz ${card.quizKey} is missing from the quiz manifest`);
  }
  return { kind: "quiz", quizSlug: quiz.slug, source: "review" };
}

async function readNextDueUserCard(
  reads: LearnCardDestinationReads,
  deprioritizeGrapheme: string | undefined,
): Promise<NextDueUserCardData | null> {
  // The next-due read is the step that decides which card comes next; if it never settles, the
  // learner is parked on the card they just rated with nothing happening.
  return watchAsyncStep("learn: readNextDueUserCard", () =>
    readThroughCancellation(() => reads.fetchNextDueUserCard({ deprioritizeGrapheme })),
  );
}

async function resolveNarrowedReviews({
  grapheme,
  reads,
  settings,
}: {
  grapheme: string | undefined;
  reads: LearnCardDestinationReads;
  settings: LearnCardDestinationSettings;
}): Promise<LearnCardDestinationResolution> {
  const nextCard = await readNextDueUserCard(reads, grapheme);
  if (nextCard !== null) {
    return { destination: reviewDestination(nextCard), dueQueueExhausted: false };
  }
  // Other card kinds remain due for ordinary study, so running out of the narrowed queue is not
  // global exhaustion.
  return {
    destination: {
      kind: "summary",
      reason: settings.voiceActive ? "voice-complete" : "review-focus-complete",
    },
    dueQueueExhausted: false,
  };
}

async function resolveAfterReview({
  grapheme,
  reads,
  settings,
}: {
  grapheme: string | undefined;
  reads: LearnCardDestinationReads;
  settings: LearnCardDestinationSettings;
}): Promise<LearnCardDestinationResolution> {
  // While reviews remain, a review leads into a new card ONLY when the learner has switched New
  // Cards on (offered once review recharge is full) and review recharge remains to spend.
  const nextCard = await readNextDueUserCard(reads, grapheme);
  if (
    nextCard !== null &&
    settings.introduceNewCardsEnabled &&
    (await isSpendingReviewRecharge(reads, settings))
  ) {
    const nextLesson = await readThroughCancellation(() => reads.fetchNextLesson());
    if (nextLesson !== null) {
      return { destination: lessonDestination(nextLesson), dueQueueExhausted: false };
    }
  }

  if (nextCard !== null) {
    return { destination: reviewDestination(nextCard), dueQueueExhausted: false };
  }

  // The due-review queue is exhausted.
  if (!settings.introduceNewCardsEnabled) {
    return {
      destination: { kind: "summary", reason: "new-cards-disabled" },
      dueQueueExhausted: true,
    };
  }

  // Same exposure as the next-due read above: this decides the route too.
  const nextLesson = await watchAsyncStep("learn: readNextLesson", () =>
    readThroughCancellation(() => reads.fetchNextLesson()),
  );
  if (nextLesson === null) {
    return {
      destination: { kind: "summary", reason: "catalogue-exhausted" },
      dueQueueExhausted: true,
    };
  }
  // Nothing to review, so review recharge does not apply: introduce the next lesson group for free (one
  // unit — its end is a lesson-group boundary like any other).
  return { destination: lessonDestination(nextLesson), dueQueueExhausted: true };
}

/** New Cards is on and review recharge remains to spend. */
async function isSpendingReviewRecharge(
  reads: LearnCardDestinationReads,
  settings: LearnCardDestinationSettings,
): Promise<boolean> {
  return (
    settings.reviewRechargeNewCardsEnabled &&
    (await readThroughCancellation(() => reads.fetchReviewRecharge())) > 0
  );
}

/**
 * Lesson groups are never split: inside one, lessons continue even with no review recharge left — the new
 * character that drained it paid for the whole unit. At a lesson-group boundary (a level boundary is one
 * too) a due review wins unless New Cards is still spending review recharge (no new character has
 * drained it yet, e.g. a unit of slides only), in which case the next unit opens. With nothing due,
 * the next unit opens. Returns null inside a unit.
 */
async function resolveLearningBoundary({
  currentLevelIndex,
  currentLessonGroupIndex,
  nextLesson,
  reads,
  settings,
}: {
  currentLevelIndex: number;
  currentLessonGroupIndex: number;
  nextLesson: Unit | null;
  reads: LearnCardDestinationReads;
  settings: LearnCardDestinationSettings;
}): Promise<LearnCardDestinationResolution | null> {
  if (!isLessonGroupBoundary({ currentLevelIndex, currentLessonGroupIndex, nextLesson })) {
    return null;
  }
  const nextReviewCard = await readNextDueUserCard(reads, undefined);
  if (
    nextReviewCard !== null &&
    (nextLesson === null || !(await isSpendingReviewRecharge(reads, settings)))
  ) {
    return { destination: reviewDestination(nextReviewCard), dueQueueExhausted: false };
  }
  return nextLesson === null
    ? null
    : { destination: lessonDestination(nextLesson), dueQueueExhausted: false };
}

async function resolveAfterLesson({
  grapheme,
  level,
  part,
  reads,
  settings,
}: {
  grapheme: string;
  level: Level;
  part: GraphemeLessonPart | undefined;
  reads: LearnCardDestinationReads;
  settings: LearnCardDestinationSettings;
}): Promise<LearnCardDestinationResolution> {
  // Anchored POSITIONALLY on the current card: the answer comes from the static sequence, so it
  // does not depend on whether the current card's enrollment has been written yet.
  const nextLesson = await readThroughCancellation(() =>
    reads.fetchNextLesson({
      afterGrapheme: {
        grapheme,
        levelIndex: level.index,
        ...(part === undefined ? {} : { part }),
      },
    }),
  );

  const boundary = await resolveLearningBoundary({
    currentLevelIndex: level.index,
    currentLessonGroupIndex: getMorphemeLessonGroupIndex({ grapheme, level, part }),
    nextLesson,
    reads,
    settings,
  });
  if (boundary !== null) {
    return boundary;
  }

  // No new characters left and nothing due — leave the lesson stack.
  if (nextLesson === null) {
    return { destination: { kind: "exit" }, dueQueueExhausted: false };
  }

  // Slides inside the unit retain their authored curriculum position; otherwise either still
  // within this unit, or the boundary had no due reviews: continue to the next lesson card.
  return { destination: lessonDestination(nextLesson), dueQueueExhausted: false };
}

async function resolveAfterSlide({
  reads,
  settings,
  slideSlug,
}: {
  reads: LearnCardDestinationReads;
  settings: LearnCardDestinationSettings;
  slideSlug: string;
}): Promise<LearnCardDestinationResolution> {
  const currentSlide = getSlideUnit(slideSlug, reads.levels);

  const resolveSlideBoundary = async (
    nextLesson: Unit | null,
  ): Promise<LearnCardDestinationResolution | null> => {
    if (currentSlide === null) {
      return null;
    }
    return resolveLearningBoundary({
      currentLevelIndex: currentSlide.levelIndex,
      currentLessonGroupIndex: currentSlide.lessonGroupIndex ?? 0,
      nextLesson,
      reads,
      settings,
    });
  };

  // While slides remain, advancing always moves to the very next slide in authored order — even
  // if already visited — so paging through a lesson stays linear. Slides the learner is already
  // PAST are skipped, and the walk stops at the first unstudied character.
  const [studiedGraphemes, pronouncedGraphemes] = await Promise.all([
    readThroughCancellation(() => reads.fetchStudiedGraphemes()),
    readThroughCancellation(() => reads.fetchPronouncedGraphemes()),
  ]);
  const nextSlideSlug = getNextSlideSlugInOrder(
    slideSlug,
    reads.levels,
    new Set(studiedGraphemes),
    pronouncedGraphemes,
  );
  if (nextSlideSlug !== null) {
    const nextSlide = getSlideUnit(nextSlideSlug, reads.levels);
    if (nextSlide === null) {
      throw new Error(`Next slide '${nextSlideSlug}' is missing from the lesson sequence.`);
    }
    const boundary = await resolveSlideBoundary(nextSlide);
    if (boundary !== null) {
      return boundary;
    }
    return { destination: { kind: "slide", lesson: nextSlide }, dueQueueExhausted: false };
  }

  // Last slide in order → resolve the boundary from the top of the sequence. The read counts the
  // slide being left as completed (see `assumedCompletedUnitKeys` above), so neither a prediction
  // made on it nor an answer cached before its completion can name the slide we are standing on.
  const nextLesson = await readThroughCancellation(() => reads.fetchNextLesson());
  const boundary = await resolveSlideBoundary(nextLesson);
  if (boundary !== null) {
    return boundary;
  }
  if (nextLesson === null) {
    return { destination: { kind: "exit" }, dueQueueExhausted: false };
  }
  return { destination: lessonDestination(nextLesson), dueQueueExhausted: false };
}

/**
 * After a quiz answered in its lesson: like the last slide of a run, the next unit comes from the
 * top of the sequence (counting this quiz as answered, see `assumedCompletedUnitKeys` above), with
 * the lesson-group boundary checked from the quiz's own position.
 */
async function resolveAfterLessonQuiz({
  quizSlug,
  reads,
  settings,
}: {
  quizSlug: string;
  reads: LearnCardDestinationReads;
  settings: LearnCardDestinationSettings;
}): Promise<LearnCardDestinationResolution> {
  const currentQuiz = getQuizUnit(quizSlug, reads.levels);
  const nextLesson = await readThroughCancellation(() => reads.fetchNextLesson());
  if (currentQuiz !== null) {
    const boundary = await resolveLearningBoundary({
      currentLevelIndex: currentQuiz.levelIndex,
      currentLessonGroupIndex: currentQuiz.lessonGroupIndex ?? 0,
      nextLesson,
      reads,
      settings,
    });
    if (boundary !== null) {
      return boundary;
    }
  }
  if (nextLesson === null) {
    return { destination: { kind: "exit" }, dueQueueExhausted: false };
  }
  return { destination: lessonDestination(nextLesson), dueQueueExhausted: false };
}

function lessonDestination(unit: Unit): LearnCardDestination {
  switch (unit.type) {
    case "slide": {
      return { kind: "slide", lesson: unit };
    }
    case "quiz": {
      return { kind: "quiz", quizSlug: unit.slug, source: "lesson" };
    }
    case "morpheme": {
      return { kind: "lesson", lesson: unit };
    }
  }
}
